import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { fetchWrapper } from '../../utils/apiHandlers'
import Button from '../Button'
import SmallButton from '../SmallButton'
import InputComp from '../InputComp'
import TextboxComp from '../TextboxComp'
import MessageModal from '../MessageModal'
import ConfirmModal from '../ConfirmModal'
import LoadingComponent from '../LoadingComponent'
import theme from '../../theme'

interface Prop {
  userid: string;
}

const ShoppingProductsComp: React.FC<Prop> = ({ userid }) => {
  const [name, setname] = useState('')
  const [price, setprice] = useState('')
  const [url, seturl] = useState('')
  const [description, setdescription] = useState('')

  const [products, setproducts] = useState<any[]>([])
  const [productlimit, setproductlimit] = useState(100)
  const [shoppingpremium, setshoppingpremium] = useState(false)
  const [loading, setloading] = useState(false)

  const [modalstate, setmodalstate] = useState(false)
  const [modalheader, setmodalheader] = useState('')
  const [modalmessage, setmodalmessage] = useState('')

  const [confirmstate, setconfirmstate] = useState(false)
  const [deleteid, setdeleteid] = useState('')

  const openModal = (header: string, message: string) => {
    setmodalheader(header)
    setmodalmessage(message)
    setmodalstate(true)
  }

  const loadProducts = async () => {
    setloading(true)
    try {
      let response = await fetchWrapper.post('/api/products/getproducts', { userid })
      setproducts(response.products || [])
      setproductlimit(response.productlimit || 100)
      setshoppingpremium(response.shoppingpremium || false)
    } catch (error) {
      openModal('Error', `${error}`)
    }
    setloading(false)
  }

  useEffect(() => {
    if (userid) loadProducts()
  }, [userid])

  const addProduct = async () => {
    if (!name.trim()) {
      openModal('Warning', 'Product name is required.')
      return
    }
    setloading(true)
    try {
      let response = await fetchWrapper.post('/api/products/addproduct', { userid, name, price, url, description })
      if (response.status !== 200) {
        openModal('Warning', response.msg)
      } else {
        setname('')
        setprice('')
        seturl('')
        setdescription('')
      }
    } catch (error) {
      openModal('Error', `${error}`)
    }
    await loadProducts()
  }

  const deleteProduct = (id: string) => {
    setdeleteid(id)
    setconfirmstate(true)
  }

  const confirmDelete = async () => {
    setloading(true)
    try {
      await fetchWrapper.post('/api/products/deleteproduct', { id: deleteid })
    } catch (error) {
      openModal('Error', `${error}`)
    }
    setconfirmstate(false)
    await loadProducts()
  }

  const usageRatio = products.length / (productlimit || 100)

  return (
    <MainContainer>
      <MessageModal state={modalstate} header={modalheader} message={modalmessage} handleClose={() => setmodalstate(false)} />
      <ConfirmModal state={confirmstate} func={confirmDelete} header="Confirm" message="Delete this product?" handleClose={() => setconfirmstate(false)} />

      <SubHeader>Products</SubHeader>
      <Description>
        Add products so your chatbot can answer questions about them - price, details, and a link straight to the product page.
      </Description>

      {!shoppingpremium && (
        <UsageBar>
          <UsageFill style={{ width: `${Math.min(usageRatio, 1) * 100}%` }} $warn={usageRatio >= 0.9} />
        </UsageBar>
      )}
      {!shoppingpremium && (
        <UsageLabel>{products.length} / {productlimit} products on the free plan</UsageLabel>
      )}
      {shoppingpremium && <UsageLabel>Premium - unlimited products</UsageLabel>}

      {loading && <LoadingComponent />}

      <FormRow>
        <InputComp id="product_name" value={name} onChange={(e: any) => setname(e.target.value)} placeholder="Product name" />
        <InputComp id="product_price" value={price} onChange={(e: any) => setprice(e.target.value)} placeholder="Price (e.g. $25)" />
      </FormRow>
      <FormRow>
        <InputComp id="product_url" value={url} onChange={(e: any) => seturl(e.target.value)} placeholder="Link to the product page" width={400} />
      </FormRow>
      <TextboxComp id="product_description" value={description} onChange={(e: any) => setdescription(e.target.value)} placeholder="Description" rows={4} cols={10} width={500} />
      <Button text="Add Product" handleClick={addProduct} />

      {products.map((p) => (
        <ProductRow key={p._id}>
          <ProductName>{p.name}{p.price ? ` — ${p.price}` : ''}</ProductName>
          {p.description && <ProductDesc>{p.description}</ProductDesc>}
          {p.url && <ProductLink href={p.url} target="_blank" rel="noreferrer">{p.url}</ProductLink>}
          <SmallButton text="Delete" handleClick={() => deleteProduct(p._id)} backgroundColor={theme.colors.danger} />
        </ProductRow>
      ))}
    </MainContainer>
  )
}

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: 8px;
`

const SubHeader = styled.div`
  padding: 10px;
  font-weight: 700;
  font-size: 28px;
  color: ${theme.colors.text};
`

const Description = styled.p`
  padding: 0 7px;
  line-height: 1.5;
  font-size: 14px;
  color: ${theme.colors.textMuted};
  width: 90%;
  max-width: 500px;
  text-align: center;
`

const UsageBar = styled.div`
  width: 90%;
  max-width: 500px;
  height: 6px;
  border-radius: 3px;
  background: ${theme.colors.border};
  overflow: hidden;
`

const UsageFill = styled.div<{ $warn: boolean }>`
  height: 100%;
  background: ${(p) => (p.$warn ? theme.colors.danger : theme.colors.primary)};
`

const UsageLabel = styled.div`
  font-size: 12px;
  color: ${theme.colors.textMuted};
`

const FormRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
`

const ProductRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  width: 90%;
  max-width: 500px;
  padding: 14px;
  border-radius: ${theme.radius.md};
  border: 1px solid ${theme.colors.border};
  background: ${theme.colors.surface};
`

const ProductName = styled.div`
  font-weight: 700;
  color: ${theme.colors.text};
`

const ProductDesc = styled.div`
  font-size: 13px;
  color: ${theme.colors.textMuted};
`

const ProductLink = styled.a`
  font-size: 12px;
  color: ${theme.colors.primary};
`

export default ShoppingProductsComp
